/**
 * EntryScreen — the initial view where the Visitor supplies Company_Input.
 *
 * Renders a minimal form with a company name input and a marketing
 * description textarea. On submit, the form defers validation to the
 * controller (which re-parses with the Zod schema) so the single source
 * of truth remains `CompanyInput` in `src/schema/roastSchema.ts`.
 *
 * The form is responsive: phones in portrait, tablets, and laptops all
 * see a usable layout driven entirely by the responsive rules in
 * `brand.css`. The previous narrow-viewport advisory has been retired
 * so phone visitors can roast their marketing too. The {@link ViewportMatcher}
 * parameter is retained for API compatibility with the existing
 * `AppShell`, but is no longer consulted.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Component Tree"
 * and Requirements 1, 8.
 */

import type { AppController } from '../state/appController';
import type { ViewportMatcher } from './NarrowViewportNotice';

/**
 * Mount the entry screen into `root`. Returns a teardown function that
 * removes DOM, detaches listeners, and unsubscribes from viewport and
 * controller events. Safe to call twice.
 */
export function mountEntryScreen(
  root: HTMLElement,
  controller: AppController,
  _viewport: ViewportMatcher,
): () => void {
  let container: HTMLElement | null = null;
  let torn = false;

  /**
   * Build the form DOM. Returns the container plus handles needed to
   * attach/clear validation messages as the user edits fields.
   */
  const buildForm = (): HTMLElement => {
    const wrapper = document.createElement('div');
    wrapper.className = 'entry-screen';

    const form = document.createElement('form');
    form.setAttribute('novalidate', '');

    // --- Company name field ---
    const nameField = document.createElement('div');
    nameField.className = 'form-field';

    const nameLabel = document.createElement('label');
    nameLabel.textContent = 'Company name';
    nameLabel.htmlFor = 'roast-company-name';

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.name = 'companyName';
    nameInput.id = 'roast-company-name';
    nameInput.maxLength = 100;
    nameInput.required = true;
    nameInput.setAttribute('aria-label', 'Company name');
    nameInput.placeholder = 'Your company name';

    nameField.appendChild(nameLabel);
    nameField.appendChild(nameInput);

    // --- Marketing description field ---
    const descField = document.createElement('div');
    descField.className = 'form-field';

    const descLabel = document.createElement('label');
    descLabel.textContent = 'How you currently market';
    descLabel.htmlFor = 'roast-marketing-description';

    const descInput = document.createElement('textarea');
    descInput.name = 'marketingDescription';
    descInput.id = 'roast-marketing-description';
    descInput.maxLength = 1000;
    descInput.required = true;
    descInput.rows = 5;
    descInput.setAttribute('aria-label', 'How you currently market');
    descInput.placeholder =
      'Tell us how you currently market your product\u2026';

    descField.appendChild(descLabel);
    descField.appendChild(descInput);

    // --- Submit ---
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.textContent = 'Roast my marketing';

    form.appendChild(nameField);
    form.appendChild(descField);
    form.appendChild(submit);
    wrapper.appendChild(form);

    /**
     * Remove any existing validation message under the given field
     * wrapper. Used when the user edits the field so previous errors
     * don't linger.
     */
    const clearFieldError = (fieldEl: HTMLElement): void => {
      const existing = fieldEl.querySelector('.roast-validation-error');
      if (existing !== null) {
        existing.remove();
      }
    };

    /**
     * Attach a validation message under the given field wrapper, replacing
     * any previous one.
     */
    const setFieldError = (fieldEl: HTMLElement, message: string): void => {
      clearFieldError(fieldEl);
      const p = document.createElement('p');
      p.className = 'roast-validation-error';
      p.setAttribute('role', 'alert');
      p.textContent = message;
      fieldEl.appendChild(p);
    };

    // Clear errors once the user edits the offending field.
    const onNameInput = (): void => clearFieldError(nameField);
    const onDescInput = (): void => clearFieldError(descField);
    nameInput.addEventListener('input', onNameInput);
    descInput.addEventListener('input', onDescInput);

    const onSubmit = (event: SubmitEvent): void => {
      event.preventDefault();
      clearFieldError(nameField);
      clearFieldError(descField);
      controller.submit({
        companyName: nameInput.value,
        marketingDescription: descInput.value,
      });
    };
    form.addEventListener('submit', onSubmit);

    // Subscribe to validation errors emitted by the controller on invalid
    // submissions. The controller guarantees this fires synchronously from
    // within `submit()`, so messages appear immediately on bad submits.
    const unsubscribeValidation = controller.onValidationError((errors) => {
      if (errors.companyName !== undefined) {
        setFieldError(nameField, errors.companyName);
      }
      if (errors.marketingDescription !== undefined) {
        setFieldError(descField, errors.marketingDescription);
      }
    });

    // Stash the teardown callbacks on the wrapper so the outer teardown
    // can reach them without closure juggling.
    (wrapper as HTMLElement & { __teardown?: () => void }).__teardown =
      () => {
        form.removeEventListener('submit', onSubmit);
        nameInput.removeEventListener('input', onNameInput);
        descInput.removeEventListener('input', onDescInput);
        unsubscribeValidation();
      };

    return wrapper;
  };

  /**
   * Mount the entry form. The form is responsive (see `brand.css`) so it
   * renders correctly on phones in portrait, tablets, and laptops — the
   * previous narrow-viewport advisory has been retired now that the app
   * supports small viewports natively.
   */
  const render = (): void => {
    if (torn) {
      return;
    }
    if (container !== null) {
      const prevTeardown = (
        container as HTMLElement & { __teardown?: () => void }
      ).__teardown;
      if (prevTeardown !== undefined) {
        prevTeardown();
      }
      container.remove();
      container = null;
    }
    const next = buildForm();
    root.appendChild(next);
    container = next;
  };

  render();

  return () => {
    if (torn) {
      return;
    }
    torn = true;
    if (container !== null) {
      const prevTeardown = (
        container as HTMLElement & { __teardown?: () => void }
      ).__teardown;
      if (prevTeardown !== undefined) {
        prevTeardown();
      }
      container.remove();
      container = null;
    }
  };
}
